import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router";
import { api } from "../data";
import type { MfaState } from "../data/api";
import { useSession } from "./session";
import { t } from "../lib/sprache";
import { ZweiFaktorVerwalten } from "../components/ZweiFaktor";
import { Coco } from "../components/States";

/**
 * Zweiter Schritt der Anmeldung: Wer einen zweiten Faktor (Authenticator-App) eingerichtet hat, gibt nach dem Passwort
 * den Code ein, bevor ein Portal öffnet. Öffentliche Seiten (Signierlink, Einladung, Insights-Link, Anmeldung) bleiben
 * erreichbar. Im Demo-Modus gibt es keinen zweiten Faktor.
 */
const OFFEN = ["/anmelden", "/intern/anmelden", "/intern/einladung", "/bestaetigen", "/unterschreiben", "/einladung", "/i/", "/kit/", "/registrieren", "/freigabe/", "/bericht/", "/einwilligung"];
const en = () => typeof navigator !== "undefined" && !navigator.language.toLowerCase().startsWith("de");

export function ZweiterFaktorGate({ children }: { children: ReactNode }) {
  const { session } = useSession();
  const mfa = api.mfa;
  const offen = typeof window !== "undefined" && OFFEN.some((p) => window.location.pathname.startsWith(p));
  const q = useQuery({
    queryKey: ["mfa-state", session?.userId],
    enabled: !!session && !!mfa && !offen,
    queryFn: () => mfa!.state(),
    staleTime: 60_000,
  });
  if (!session || !mfa || offen || !q.data) return <>{children}</>;
  const needs = q.data.next === "aal2" && q.data.current === "aal1";
  const factor = q.data.factors.find((f) => f.verified);
  if (!needs || !factor) return <>{children}</>;
  return <CodeAbfrage factorId={factor.id} />;
}

function CodeAbfrage({ factorId }: { factorId: string }) {
  const qc = useQueryClient();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const E = en();
  async function send(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setFehler(null);
    try {
      await api.mfa!.verify(factorId, code);
      await qc.invalidateQueries({ queryKey: ["mfa-state"] });
    } catch {
      setFehler(E ? "The code is not valid. Please enter the current 6-digit code from your authenticator app." : "Der Code stimmt nicht. Bitte den aktuellen sechsstelligen Code aus deiner Authenticator-App eingeben.");
      setCode("");
    } finally {
      setBusy(false);
    }
  }
  return (
    // Mittig mit Coco wie die übrigen Anmeldeseiten (Inhaber 06.10.2026)
    <main id="main" className="content tk-faktor">
      <form className="panel stack tk-faktor-karte" onSubmit={(e) => void send(e)} aria-busy={busy}>
        <div className="tk-faktor-kopf">
          <Coco pose="hinweis" size={96} />
          <p className="eyebrow">{E ? "Second step" : "Zweiter Schritt"}</p>
          <h1>{E ? "Two-step sign-in" : "Anmeldung bestätigen"}</h1>
          <p>{E ? "Enter the 6-digit code from your authenticator app." : "Gib den sechsstelligen Code aus deiner Authenticator-App ein."}</p>
        </div>
        <label>
          {E ? "Code" : "Code"}
          <input className="tk-faktor-code" placeholder="123 456" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9 ]{6,7}" maxLength={7} required autoFocus
            value={code} onChange={(e) => setCode(e.target.value)} />
        </label>
        {fehler && <p className="tk-error" role="alert">{fehler}</p>}
        <div className="tk-faktor-knoepfe">
          <button className="primary" type="submit" disabled={busy || code.replace(/\s+/g, "").length !== 6}>{E ? "Confirm" : "Bestätigen"}</button>
          <button type="button" onClick={() => void api.signOut()}>{E ? "Sign out" : "Abmelden"}</button>
        </div>
        <small className="tk-faktor-hilfe">{E ? "Lost your phone? Contact the CreatorOffice platform support (directly or via your agency's chief manager). After checking your identity, it resets the second factor; you then sign in with your password and set up a new one."
          : "Handy verloren? Melde dich beim Plattform-Betrieb von CreatorOffice (direkt oder über den Admin deiner Agentur). Nach Prüfung deiner Identität setzt er den zweiten Faktor zurück; danach meldest du dich mit Passwort an und richtest einen neuen ein."}</small>
      </form>
    </main>
  );
}

/**
 * Pflicht zum zweiten Faktor ohne eingerichteten Faktor (RECHTE-9): Abmelden und neu Anmelden hilft dann nicht, die Person
 * muss erst einen Faktor einrichten. Hinweis im Agentur-Portal, solange das so ist.
 */
export function brauchtFaktorEinrichtung(pflicht: boolean | undefined, state: MfaState | undefined): boolean {
  return !!pflicht && !!state && !state.factors.some((f) => f.verified);
}
export function ZweiFaktorPflichtHinweis({ orgId }: { orgId: string }) {
  const { session } = useSession();
  const mfa = api.mfa;
  const pflicht = useQuery({
    queryKey: ["org_settings", orgId, "require_mfa"],
    enabled: !!mfa,
    queryFn: () => api.select<{ require_mfa: boolean }>("org_settings", { columns: "require_mfa", filters: [["org_id", "eq", orgId]] }).then((r) => r[0]?.require_mfa ?? false),
    staleTime: 60_000,
  });
  const state = useQuery({ queryKey: ["mfa-state", session?.userId], enabled: !!session && !!mfa && pflicht.data === true, queryFn: () => mfa!.state(), staleTime: 60_000 });
  if (!brauchtFaktorEinrichtung(pflicht.data, state.data)) return null;
  return (
    <div className="content tk-rahmen-hinweise">
      <div className="notice" role="status">
        {t("Deine Agentur verlangt für Auszahlungen, Bankverbindungen und Zahlungseingänge einen zweiten Faktor, und du hast noch keinen eingerichtet.", "Your agency requires a second factor for payouts, bank details and incoming payments, and you have not set one up yet.")}{" "}
        <Link to="/agentur/einstellungen/sicherheit">{t("Jetzt unter Anmeldung & Sicherheit einrichten", "Set it up now under Sign-in & security")}</Link>
      </div>
    </div>
  );
}

/**
 * Betreiber-Konsole (RECHTE-10, 0085): Die Rechte des Plattform-Betriebs gelten nur mit bestätigtem zweiten Faktor. Ohne
 * eingerichteten Faktor zeigt /intern zuerst die Einrichtung (danach fragt ZweiterFaktorGate den Code ab). Im Demo-Modus
 * gibt es keinen zweiten Faktor; dort und bei abgeschalteter Pflicht (platform_admins.mfa_required) öffnet die Konsole direkt.
 */
export function betriebBrauchtFaktor(pflicht: boolean | undefined, state: MfaState | undefined): boolean {
  return pflicht !== false && !!state && !state.factors.some((f) => f.verified);
}
export function BetriebFaktorPflicht({ pflicht, children }: { pflicht: boolean | undefined; children: ReactNode }) {
  const { session } = useSession();
  const mfa = api.mfa;
  const state = useQuery({ queryKey: ["mfa-state", session?.userId], enabled: !!session && !!mfa && pflicht !== false, queryFn: () => mfa!.state(), staleTime: 60_000 });
  if (!mfa || pflicht === false) return <>{children}</>;
  if (state.isPending) return <main id="main" className="content"><p className="muted">{t("Wird geladen …", "Loading …")}</p></main>;
  if (!betriebBrauchtFaktor(pflicht, state.data)) return <>{children}</>;
  return (
    <main id="main" className="content stack">
      <div className="notice warning" role="alert">
        {t("Die Betreiber-Konsole öffnet nur mit zweitem Faktor: Sie zeigt Konten, Kontakte und Leads und kann Konten verändern. Richte ihn hier ein und melde dich danach mit dem Code an.",
          "The operator console only opens with a second factor: it shows accounts, contacts and leads and can change accounts. Set it up here and then sign in with the code.")}
      </div>
      <ZweiFaktorVerwalten headingId="betrieb-zwei-faktor" />
    </main>
  );
}
